import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendar,
  PiCheck,
  PiClockCounterClockwise,
  PiNavigationArrow,
  PiPlus,
  PiTrash,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetContractsByLabel } from 'services/contract';
import { PRODUCT_TYPE } from 'services/contract/types';
import { useGetLabelLightList } from 'services/labels/light';
import { IMerchMutationForm } from 'services/merch/types';
import { ISettlementInfoContractForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate, formatDateToInput } from 'utils/global/date.utils';

interface IMerchContractsSeletcProps extends ISettlementInfoContractForm {
  id: number;
}

const MerchContractsSeletc = ({
  id,
  contractId,
  contractName,
  labelId,
}: IMerchContractsSeletcProps) => {
  const { values, setFieldValue }: IFormikContextType<IMerchMutationForm> =
    useFormikContext<IMerchMutationForm>();
  const { t } = useTranslation();

  usePermissionGuard('ContractPermissionsLIST**');

  const labelContracts = useGetContractsByLabel(labelId, {
    params: {
      itemCount: 999,
      page: 0,
      ProductType: PRODUCT_TYPE.Merch,
      ShowOnlyActive: true,
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const contractOptions =
    labelContracts.data?.pages
      .flat()
      .filter(
        labelContract =>
          !values.contracts.map(c => c.contractId).includes(labelContract.id)
      )
      .map(contract => ({ value: contract.id, label: contract.title })) ?? [];

  useEffect(() => {
    labelContracts.refetch();
  }, [labelId]);

  return (
    <FormInput.Select
      name={`contracts[${id}].contractId`}
      isAsync
      isSearchable
      label={t(`input.label.contract`)}
      onChange={value => {
        setFieldValue(`contracts[${id}].contractName`, value.label);
      }}
      value={{
        label: contractName,
        value: contractId,
      }}
      options={contractOptions}
      isLoading={labelContracts.isLoading}
    />
  );
};

const MerchContracts = () => {
  const navigateToNewWindow = useNewWindowNavigate();

  const navigate = useNavigate();

  const {
    values,
    setFieldValue,
    status,
    initialValues,
  }: IFormikContextType<IMerchMutationForm> =
    useFormikContext<IMerchMutationForm>();

  const { t } = useTranslation();

  const labels = useGetLabelLightList();
  const { applicationSettings } = useStoreState();

  const getLabelOptions = (contracts: ISettlementInfoContractForm[]) =>
    labels.data
      ?.filter(label =>
        applicationSettings?.allowContractsWithSameLabel
          ? true
          : !contracts.map(contract => contract.labelId).includes(label.id)
      )
      .map(label => ({ value: label.id, label: label.labelName })) ?? [];

  const getDivisionPercentageSum = (contracts: ISettlementInfoContractForm[]) =>
    contracts
      .map(contract => contract.divisionPercentage ?? 0)
      .reduce((accumulator, currentValue) => accumulator + currentValue, 0) ??
    0;

  const getContractsWithoutDivision = (
    contracts: ISettlementInfoContractForm[]
  ) =>
    contracts.filter(
      ({ divisionPercentage }) =>
        divisionPercentage === null || divisionPercentage === undefined
    ).length ?? 1;

  const getPercentageLeft = (contracts: ISettlementInfoContractForm[]) =>
    getDivisionPercentageSum(contracts) < 100
      ? parseFloat(
          (
            (100 - getDivisionPercentageSum(contracts)) /
            getContractsWithoutDivision(contracts)
          ).toFixed(2)
        )
      : 0;

  const onAddContract = () => {
    setFieldValue(`contracts[${values.contracts?.length ?? 0}]`, {
      contractId: '',
      contractName: '',
      labelId: '',
      labelName: '',
      startDate: formatDateToInput(new Date()),
      endDate: null,
      divisionPercentage: null,
    });
  };

  const onRemoveContract = (index: number) => {
    setFieldValue(
      'contracts',
      values.contracts.filter((_, key) => key !== index)
    );
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          hideHeader
          topbarContent={
            <div className='flex w-full justify-end'>
              <div className='flex gap-2'>
                <Button
                  onlyIcon
                  isClear
                  onClick={() => navigate('history')}
                  disabled={!initialValues.EAN.length}>
                                    <Button.Description description={t('history.contract')} />

                                    <Button.Icon icon={<PiClockCounterClockwise />} />
                </Button>
                <Button
                  onlyIcon
                  isClear
                  onClick={() => navigate('future')}
                  disabled={!initialValues.EAN.length}>
                                    <Button.Description description={t('history.contract-future')} />

                                    <Button.Icon icon={<PiCalendar />} />
                </Button>
              </div>
            </div>
          }
        />
      }>
      <Table
        id={'MerchContracts'}
        size='compact'
        data={{
          nodes:
            values.contracts?.map((_, i) => ({
              ..._,
              id: i,
            })) ?? [],
        }}
        columns={[
          {
            label: `${t('input.label.global')}`,
            renderCell: ({ labelName: name, labelId: id, id: index }) => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={name} />
                ) : (
                  <FormInput.Select
                    name={`contracts[${index}].labelId`}
                    isAsync
                    isSearchable
                    label={t(`input.label.label`)}
                    onChange={value => {
                      setFieldValue(
                        `contracts[${index}].labelName`,
                        value.label
                      );
                      setFieldValue(`contracts[${index}].contractName`, ' ');
                      setFieldValue(`contracts[${index}].contractId`, ' ');
                    }}
                    value={{
                      label: name,
                      value: id,
                    }}
                    options={getLabelOptions(values.contracts ?? [])}
                    isLoading={labels.isLoading}
                  />
                )}
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t(`input.label.contract`),
            renderCell: contract => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={contract.contractName} />
                ) : !contract.labelId.length ? (
                  <FormInput.Select
                    name={`contracts[${contract.id}].contractId`}
                    label={t(`input.label.contract`)}
                    isDisabled
                  />
                ) : (
                  <MerchContractsSeletc {...contract} />
                )}
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t(`input.label.date`),
            renderCell: contract => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={`${formatDate(contract.startDate)}`} />
                ) : (
                  <FormInput.Text
                    name={`contracts[${contract.id}].startDate`}
                    type='date'
                    label={t('input.label.startDate')}
                  />
                )}
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t(`input.label.division`),
            renderCell: ({ divisionPercentage, id: index }) => (
              <TableItem>
                {status?.displayMode ? (
                  <TableItem.Text text={`${divisionPercentage ?? '-'}%`} />
                ) : (
                  <div className='flex w-full gap-2'>
                    <div>
                      <FormInput.Number
                        name={`contracts[${index}].divisionPercentage`}
                        label={t(`input.label.division`)}
                        suffix=' %'
                        placeholder={`${getPercentageLeft(
                          values.contracts ?? []
                        )} %`}
                      />
                    </div>
                    {values.contracts[index].divisionPercentage === null && (
                      <Button
                        size='compact'
                        isClear
                        onlyIcon
                        onClick={() =>
                          setFieldValue(
                            `contracts[${index}].divisionPercentage`,
                            getPercentageLeft(values.contracts ?? [])
                          )
                        }>
                                                <Button.Description description={t('button.apply')} />

                                                <Button.Icon icon={<PiCheck />} />
                      </Button>
                    )}
                  </div>
                )}
              </TableItem>
            ),
          },
          {
            label: '',
            renderCell: ({ id: index, labelId, contractId }) => (
              <TableItem justifyPosition='end'>
                {status?.displayMode ? (
                  <Button
                    isClear
                    onlyIcon
                    onClick={() =>
                      navigateToNewWindow(
                        `label/${labelId}/contract/${contractId}/form`
                      )
                    }>
                                        <Button.Description description={t('button.go-to-form')} />

                                        <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                ) : (
                  <Button
                    variant={BUTTON_VARIANT.remove}
                    isClear
                    onlyIcon
                    onClick={() => onRemoveContract(index)}>
                                        <Button.Description description={t('button.remove')} />

                                        <Button.Icon icon={<PiTrash />} />
                  </Button>
                )}
              </TableItem>
            ),
          },
        ]}
        tableOptions={
          status?.displayMode
            ? undefined
            : {
                renderAfterTable: () => (
                  <tfoot className='full-width-footer'>
                    <TableMessage
                      className='cursor-pointer'
                      onClick={onAddContract}>
                      <TableItem.Text text={t('button.add')} />
                      <PiPlus />
                    </TableMessage>
                  </tfoot>
                ),
              }
        }
      />
    </TopbarPage>
  );
};

export default MerchContracts;
